/**
 * PipelineQuickEditModal - Lightweight modal for in-place field edits from Pipeline
 *
 * Receives a workItem (entity_kind, entity_id) + action (with fields array),
 * fetches the current entity, shows only the relevant fields, and PUTs back.
 * On success, calls onSuccess so the parent can mark the work item DONE.
 */
import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, AlertCircle } from 'lucide-react';
import { driverAPI } from '../../api/drivers.js';
import { vehicleAPI } from '../../api/vehicles.js';
import { claimAPI } from '../../api/claims.js';
import { insuranceAPI } from '../../api/insurance.js';

// Field config: label, input type, placeholder
const FIELD_CONFIG = {
  phone: { label: 'Telephone', type: 'tel', placeholder: '+33 6 00 00 00 00' },
  email: { label: 'Email', type: 'email', placeholder: 'nom@exemple.com' },
  license_number: { label: 'N° permis', type: 'text', placeholder: 'Numero de permis' },
  license_expiration_date: { label: 'Expiration permis', type: 'date', placeholder: '' },
  vin: { label: 'VIN', type: 'text', placeholder: '17 caracteres' },
  current_mileage: { label: 'Kilometrage actuel', type: 'number', placeholder: 'km' },
  description: { label: 'Description', type: 'textarea', placeholder: 'Description...' },
  end_date: { label: 'Date de fin', type: 'date', placeholder: '' },
  premium_amount: { label: 'Montant prime', type: 'number', placeholder: '0.00' },
  first_name: { label: 'Prenom', type: 'text', placeholder: 'Prenom' },
  last_name: { label: 'Nom', type: 'text', placeholder: 'Nom' },
  // La colonne s'appelle license_plate. `registration_number` est conserve pour les
  // nudges deja enregistres en base avant le 2026-08-04 — ne rien saisir de nouveau dessus.
  license_plate: { label: 'Immatriculation', type: 'text', placeholder: 'AA-123-BB' },
  registration_number: { label: 'Immatriculation', type: 'text', placeholder: 'AA-123-BB' },
  brand: { label: 'Marque', type: 'text', placeholder: 'Marque' },
  model: { label: 'Modèle', type: 'text', placeholder: 'Modèle' },
  assigned_driver_id: { label: 'Conducteur', type: 'driver_select', placeholder: '' },
};

// Entity kind -> API module mapping
const ENTITY_API_MAP = {
  driver: { api: driverAPI, label: 'Conducteur' },
  vehicle: { api: vehicleAPI, label: 'Vehicule' },
  claim: { api: claimAPI, label: 'Sinistre' },
  insurance_contract: { api: insuranceAPI, label: 'Contrat' },
};

/**
 * Build a human-readable description for an entity
 */
function getEntityDescription(entityKind, entity) {
  if (!entity) return '';
  switch (entityKind) {
    case 'vehicle':
      return [entity.license_plate, entity.make || entity.brand, entity.model]
        .filter(Boolean).join(' - ') || `Vehicule #${entity.id}`;
    case 'driver':
      return [entity.first_name, entity.last_name].filter(Boolean).join(' ')
        || `Conducteur #${entity.id}`;
    case 'claim':
      return [entity.reference, entity.claim_type, entity.status]
        .filter(Boolean).join(' - ') || `Sinistre #${entity.id}`;
    case 'insurance_contract':
      return [entity.contract_number || entity.policy_number, entity.insurer_name, entity.insurance_type]
        .filter(Boolean).join(' - ') || `Contrat #${entity.id}`;
    default:
      return `#${entity.id}`;
  }
}

function PipelineQuickEditModal({ isOpen, onClose, workItem, action, onSuccess }) {
  const [entity, setEntity] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [drivers, setDrivers] = useState([]);

  const entityKind = workItem?.entity_kind;
  const entityId = workItem?.entity_id;
  const fields = action?.fields || [];
  const apiConfig = ENTITY_API_MAP[entityKind];
  const needsDriverSelect = fields.includes('assigned_driver_id');

  // Fetch entity on open
  useEffect(() => {
    if (!isOpen || !entityId || !apiConfig) return;

    let cancelled = false;
    setLoading(true);
    setError('');
    setDrivers([]);

    const fetches = [apiConfig.api.getById(entityId)];
    // Also fetch drivers if we need a driver select
    if (needsDriverSelect) {
      fetches.push(driverAPI.getAll());
    }

    Promise.all(fetches)
      .then(([entityRes, driversRes]) => {
        if (cancelled) return;
        const data = entityRes.data || entityRes;
        setEntity(data);
        // Pre-populate form with current values for the requested fields
        const initial = {};
        fields.forEach(f => {
          initial[f] = data[f] ?? '';
        });
        setFormData(initial);

        if (driversRes) {
          const driverList = driversRes.data || driversRes;
          setDrivers(Array.isArray(driverList) ? driverList : []);
        }
      })
      .catch(err => {
        if (!cancelled) setError('Impossible de charger les donnees');
        console.error('QuickEdit fetch error:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, entityId, entityKind]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      // Data layer uses PUT — merge changed fields into full entity
      const merged = { ...entity, ...formData };
      // Remove internal fields that shouldn't be sent
      delete merged.id;
      delete merged.created_at;
      delete merged.updated_at;

      await apiConfig.api.update(entityId, merged);

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('QuickEdit save error:', err);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const title = action?.label || 'Mise a jour rapide';
  const entityDesc = getEntityDescription(entityKind, entity);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h3 className="font-semibold text-slate-800">{title}</h3>
            <p className="text-sm text-slate-500">{entityDesc}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 rounded-md transition text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              <span className="ml-2 text-slate-500">Chargement...</span>
            </div>
          ) : error && !entity ? (
            <div className="flex items-center gap-2 text-red-600 py-4">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          ) : (
            <>
              {fields.map(field => {
                const config = FIELD_CONFIG[field] || {
                  label: field.replace(/_/g, ' '),
                  type: 'text',
                  placeholder: '',
                };
                const value = formData[field] ?? '';

                // Driver select dropdown
                if (config.type === 'driver_select') {
                  return (
                    <div key={field}>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {config.label}
                      </label>
                      <select
                        value={value}
                        onChange={e => handleChange(field, e.target.value ? Number(e.target.value) : '')}
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm
                                   focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- Sélectionner un conducteur --</option>
                        {drivers
                          .filter(d => d.status === 'active' || !d.status)
                          .sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`))
                          .map(d => (
                            <option key={d.id} value={d.id}>
                              {d.last_name} {d.first_name}
                              {d.availability_status === 'AVAILABLE' ? ' ✓' : ''}
                              {d.availability_status === 'UNAVAILABLE_PLANNED' ? ' (indispo.)' : ''}
                              {d.availability_status === 'UNAVAILABLE_UNPLANNED' ? ' (absent)' : ''}
                            </option>
                          ))
                        }
                      </select>
                      {drivers.length === 0 && !loading && (
                        <p className="text-xs text-slate-400 mt-1">Aucun conducteur disponible</p>
                      )}
                    </div>
                  );
                }

                // Textarea
                if (config.type === 'textarea') {
                  return (
                    <div key={field}>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {config.label}
                      </label>
                      <textarea
                        value={value}
                        onChange={e => handleChange(field, e.target.value)}
                        placeholder={config.placeholder}
                        rows={3}
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm
                                   focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  );
                }

                // Standard input
                return (
                  <div key={field}>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {config.label}
                    </label>
                    <input
                      type={config.type}
                      value={value}
                      onChange={e => handleChange(field, config.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
                      placeholder={config.placeholder}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm
                                 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                );
              })}

              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {entity && (
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-200">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md
                        hover:bg-blue-700 transition text-sm font-medium
                        disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Enregistrer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PipelineQuickEditModal;
